import { LETTERS, timing, variantForLevel, } from './params';
/** Hash LCG déterministe à partir d'un seed entier. */
function lcg(seed) {
    const s = (Math.imul(seed | 0, 1664525) + 1013904223) >>> 0;
    return s / 0x100000000;
}
function rawLetterAt(trialIdx) {
    const r = lcg(trialIdx * 7 + 23);
    return LETTERS[Math.floor(r * LETTERS.length)];
}
/**
 * Décide si l'essai courant est un target (match n-back) en fonction d'un
 * seuil sur un seed combinant trialIdx et nBack. Utilise un seed dédié pour
 * être indépendant de la lettre. Les targets ne sont possibles que pour
 * trialIdx >= nBack.
 */
function isTargetTrial(trialIdx, nBack, targetRatio) {
    if (trialIdx < nBack)
        return false;
    const r = lcg(trialIdx * 41 + nBack * 13 + 5);
    return r < targetRatio;
}
/**
 * Calcule la lettre AFFICHÉE au trial t pour un n-back donné, en chaînant
 * récursivement : la référence n-back est la lettre qu'on a affichée à t-N
 * (et non la lettre brute du seed). Sans ce chaînage, un foil substitué à
 * t-N ferait diverger la référence et casserait le contrat n-back côté
 * joueur (le test gameplay.test.ts > "refLetter cohérente …" gardait ça).
 */
function displayedLetterAt(trialIdx, variant) {
    const n = variant.nBack;
    if (trialIdx < n)
        return rawLetterAt(trialIdx);
    const ref = displayedLetterAt(trialIdx - n, variant);
    const target = isTargetTrial(trialIdx, n, variant.targetRatio);
    if (target)
        return ref;
    const candidate = rawLetterAt(trialIdx);
    if (candidate !== ref)
        return candidate;
    // Collision : décale d'une lettre pour garantir un vrai foil reproductible.
    const idx = LETTERS.indexOf(candidate);
    return LETTERS[(idx + 1) % LETTERS.length];
}
export function makeStimulus(trialIdx, durationMs, level) {
    const variant = variantForLevel(level);
    const nBack = variant.nBack;
    const target = isTargetTrial(trialIdx, nBack, variant.targetRatio);
    const letter = displayedLetterAt(trialIdx, variant);
    const refLetter = trialIdx >= nBack ? displayedLetterAt(trialIdx - nBack, variant) : null;
    return {
        trialIdx,
        durationMs: Math.max(200, durationMs),
        letter,
        refLetter,
        isTarget: target,
        nBack,
    };
}
export function createJuggleFactorAdapter(level = 1) {
    let state = {
        subPhase: 'idle',
        stim: null,
        feedback: null,
        level,
        variant: variantForLevel(level),
        trialIdx: 0,
    };
    const listeners = new Set();
    let resolver = null;
    let rejecter = null;
    let trialStart = 0;
    const timers = [];
    function clearTimers() {
        while (timers.length > 0) {
            const t = timers.pop();
            if (t !== undefined)
                clearTimeout(t);
        }
    }
    function update(patch) {
        state = { ...state, ...patch };
        for (const l of listeners) {
            try {
                l(state);
            }
            catch {
                /* ignored */
            }
        }
    }
    function settle(answer, stim) {
        if (!resolver)
            return;
        const rt = Date.now() - trialStart;
        const correct = (answer === 'match') === stim.isTarget;
        const r = resolver;
        resolver = null;
        rejecter = null;
        r({ answer, reactionTimeMs: rt, correct });
    }
    return {
        mount() {
            // Idempotent.
        },
        unmount() {
            clearTimers();
            listeners.clear();
            if (rejecter) {
                rejecter(new Error('unmount'));
                rejecter = null;
                resolver = null;
            }
        },
        presentStimulus(stim) {
            clearTimers();
            return new Promise((resolve, reject) => {
                resolver = resolve;
                rejecter = reject;
                update({
                    subPhase: 'fixation',
                    stim,
                    feedback: null,
                    trialIdx: stim.trialIdx,
                });
                timers.push(setTimeout(() => {
                    update({ subPhase: 'stimulus' });
                    trialStart = Date.now();
                    timers.push(setTimeout(() => {
                        update({ subPhase: 'awaiting_input' });
                        timers.push(setTimeout(() => {
                            if (!resolver)
                                return;
                            const r = resolver;
                            resolver = null;
                            rejecter = null;
                            // Pas de réponse : on compte comme "no-match" (omission).
                            const fallback = 'no-match';
                            r({
                                answer: fallback,
                                reactionTimeMs: timing.responseWindowMs,
                                correct: !stim.isTarget,
                            });
                        }, timing.responseWindowMs));
                    }, stim.durationMs));
                }, timing.fixationMs));
            });
        },
        async showFeedback(correct) {
            update({
                subPhase: 'feedback',
                feedback: correct ? 'correct' : 'incorrect',
            });
            await new Promise((resolve) => {
                timers.push(setTimeout(() => {
                    update({ subPhase: 'iti' });
                    resolve();
                }, timing.feedbackMs));
            });
        },
        pause() {
            // Sprint 0.5 : no-op.
        },
        resume() {
            // Sprint 0.5 : no-op.
        },
        abort() {
            clearTimers();
            if (rejecter) {
                rejecter(new Error('aborted'));
                rejecter = null;
                resolver = null;
            }
        },
        getState() {
            return state;
        },
        subscribe(listener) {
            listeners.add(listener);
            listener(state);
            return () => {
                listeners.delete(listener);
            };
        },
        handleAnswer(answer) {
            if (state.subPhase !== 'awaiting_input' && state.subPhase !== 'stimulus') {
                return;
            }
            const stim = state.stim;
            if (!stim)
                return;
            clearTimers();
            settle(answer, stim);
        },
        setLevel(newLevel) {
            const v = variantForLevel(newLevel);
            update({ level: newLevel, variant: v });
        },
    };
}
//# sourceMappingURL=adapter.js.map